import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { FaWater, FaBell, FaArrowRight, FaCog } from 'react-icons/fa';
import { PiFan } from 'react-icons/pi';
import { TfiReload } from 'react-icons/tfi';
import AppLayout from '../layouts/AppLayout';
import useAuth from '../hooks/useAuth';
import useActiveCondominio from '../hooks/useActiveCondominio';
import useDadosCondominio from '../hooks/useDadosCondominio';
import { useFeatures } from '../hooks/useFeatures';
import './Home.css';

const Home = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { user } = useAuth();
  const { activeCondominio } = useActiveCondominio();
  const { loading: featuresLoading, hasAccessToFeature } = useFeatures();

  const isDashboardEnabled     = !featuresLoading && hasAccessToFeature('dashboard');
  const isReservatoriosEnabled = !featuresLoading && hasAccessToFeature('reservatorios');
  const isExaustoresEnabled    = !featuresLoading && hasAccessToFeature('exaustores');
  const isAlertasEnabled       = !featuresLoading && hasAccessToFeature('alertas');
  const podeVerConfig          = user?.cargo === 'admin' || user?.cargo === 'sindico';

  const [isLoading, setIsLoading] = useState(false);

  const [reservData, setReservData] = useState({
    total: 0, ok: 0, abaixoMin: 0, acimaMax: 0, semDados: 0, ultimaLeitura: null,
  });

  const [exaustData, setExaustData] = useState({
    totalExaustores: 0, agendamentosAtivos: 0,
    torresComAgendamento: 0, torresConsultadas: 0, atualizacao: null,
  });

  const [alertasData, setAlertasData] = useState({
    totalOffline: 0, ultimaVerificacao: null,
  });

  const { blocos: blocosReserv } = useDadosCondominio({ projection: 'reservatorios.v1' });
  const { blocos: blocosExaust, exaustoresPorBloco } = useDadosCondominio({ projection: 'exaustores.v1' });

  const formatarDataHora = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
  };

  // ── Carregadores ──────────────────────────────────────────────────────────

  const carregarReservatorios = useCallback(async () => {
    if (!isReservatoriosEnabled || !activeCondominio || !Array.isArray(blocosReserv) || blocosReserv.length === 0) {
      setReservData({ total: 0, ok: 0, abaixoMin: 0, acimaMax: 0, semDados: 0, ultimaLeitura: null });
      return;
    }

    const resultados = await Promise.allSettled(
      blocosReserv.map((b) =>
        axios.get(`${apiUrl}/api/reservatorios/blocos/${b.id}`, {
          headers: { 'x-condominio-id': activeCondominio },
        })
      )
    );

    let total = 0, ok = 0, abaixoMin = 0, acimaMax = 0, semDados = 0;
    let latestTs = null;

    resultados.forEach((r) => {
      if (r.status !== 'fulfilled') return;
      const modulos = r.value?.data?.dados;
      if (!Array.isArray(modulos)) return;
      modulos.forEach((modulo) => {
        if (!Array.isArray(modulo.channels)) return;
        modulo.channels.forEach((ch) => {
          total++;
          if (ch.ultimaLeitura) {
            const ts = new Date(ch.ultimaLeitura).getTime();
            if (!latestTs || ts > latestTs) latestTs = ts;
          }
          if (ch.nivelPorcentagem === 'Indefinido' || ch.nivelPorcentagem == null) {
            semDados++;
            return;
          }
          const nivel  = Number(ch.nivelPorcentagem);
          const minimo = ch.nivel_minimo != null ? Number(ch.nivel_minimo) : null;
          const maximo = ch.nivel_maximo != null ? Number(ch.nivel_maximo) : null;
          if (minimo !== null && nivel < minimo)      abaixoMin++;
          else if (maximo !== null && nivel > maximo) acimaMax++;
          else                                        ok++;
        });
      });
    });

    setReservData({
      total, ok, abaixoMin, acimaMax, semDados,
      ultimaLeitura: latestTs ? new Date(latestTs).toISOString() : null,
    });
  }, [activeCondominio, apiUrl, blocosReserv, isReservatoriosEnabled]);

  const carregarExaustores = useCallback(async () => {
    if (!isExaustoresEnabled || !activeCondominio || !Array.isArray(blocosExaust) || blocosExaust.length === 0) {
      setExaustData({ totalExaustores: 0, agendamentosAtivos: 0, torresComAgendamento: 0, torresConsultadas: 0, atualizacao: null });
      return;
    }

    const ids = blocosExaust.map((b) => b.id).filter(Boolean);
    const resultados = await Promise.allSettled(
      ids.map((blocoId) =>
        axios.get(`${apiUrl}/api/exaustores/temporizadores`, {
          params: { blocoId },
          headers: { 'x-condominio-id': activeCondominio },
        })
      )
    );

    let agendamentosAtivos = 0;
    let torresComAgendamento = 0;
    resultados.forEach((r, index) => {
      if (r.status !== 'fulfilled') return;
      const blocoId = ids[index];
      const times = r.value?.data?.dados?.remainingTimes || {};
      const grupos = exaustoresPorBloco[blocoId] || [];

      // Conta por entidade fisica (exaustor), nao por final: finais que
      // compartilham o mesmo exaustor sao o mesmo agendamento no banco
      // (uk_desligamentos_canal), entao nao devem ser contados 2x.
      const gruposComAgendamentoAtivo = grupos.filter((grupo) =>
        (grupo.finais || []).some((final) => Number(times[`final${final}`]) > 0)
      ).length;

      agendamentosAtivos += gruposComAgendamentoAtivo;
      if (gruposComAgendamentoAtivo > 0) torresComAgendamento += 1;
    });

    // Conta exaustores fisicos (entidades), nao finais controlaveis.
    const totalExaustores = Object.values(exaustoresPorBloco || {}).reduce(
      (acc, grupos) => acc + (Array.isArray(grupos) ? grupos.length : 0), 0
    );

    setExaustData({
      totalExaustores, agendamentosAtivos, torresComAgendamento,
      torresConsultadas: ids.length, atualizacao: new Date().toISOString(),
    });
  }, [activeCondominio, apiUrl, blocosExaust, exaustoresPorBloco, isExaustoresEnabled]);

  const carregarAlertas = useCallback(async () => {
    if (!isAlertasEnabled || !activeCondominio) {
      setAlertasData({ totalOffline: 0, ultimaVerificacao: null });
      return;
    }
    try {
      const res = await axios.get(`${apiUrl}/api/monitoramento/problemas/globais`, {
        headers: { 'x-condominio-id': activeCondominio },
      });
      if (res.data?.sucesso) {
        const { estatisticas = {}, problemas = {} } = res.data.dados || {};
        setAlertasData({
          totalOffline: estatisticas.total || 0,
          ultimaVerificacao: problemas.ultimaVerificacao || null,
        });
      }
    } catch {
      setAlertasData({ totalOffline: 0, ultimaVerificacao: null });
    }
  }, [activeCondominio, apiUrl, isAlertasEnabled]);

  const carregarTudo = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([carregarReservatorios(), carregarExaustores(), carregarAlertas()]);
    setIsLoading(false);
  }, [carregarReservatorios, carregarExaustores, carregarAlertas]);

  useEffect(() => { carregarTudo(); }, [carregarTudo]);

  // ── Status derivado dos cards ──────────────────────────────────────────────
  const reservStatus  = reservData.abaixoMin > 0  ? 'danger'  : reservData.acimaMax > 0 ? 'warning' : 'success';
  const alertasStatus = alertasData.totalOffline > 0 ? 'danger' : 'success';

  // ── Redirecionamento quando dashboard está desativado ─────────────────────
  if (!featuresLoading && !isDashboardEnabled) {
    if (isExaustoresEnabled)    return <Navigate to="/exaustao"      replace />;
    if (isReservatoriosEnabled) return <Navigate to="/reservatorios" replace />;
    return (
      <AppLayout titulo="Sem acesso" cargo={user?.cargo} problemas={[]}>
        <div className="home-empty-state">
          Nenhuma funcionalidade está disponível para este condomínio.
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout titulo="Dashboard" cargo={user?.cargo} problemas={[]}>
      <div className="home-page">

        {/* ── Hero ── */}
        <section className="home-hero">
          <div className="home-hero__toprow">
            <div className="home-hero__live">
              <span className="home-hero__dot" />
              Sistema ativo
            </div>
          </div>

          <div>
            <h1 className="home-hero__title">Visão Geral</h1>
          </div>

          <div className="home-hero__shortcuts">
            <span className="home-hero__shortcut-label">Acesso rápido</span>
            {isExaustoresEnabled && (
              <Link to="/exaustao" className="home-hero__pill">
                <PiFan /> Exaustores
              </Link>
            )}
            {isReservatoriosEnabled && (
              <Link to="/reservatorios" className="home-hero__pill">
                <FaWater /> Reservatórios
              </Link>
            )}
            {podeVerConfig && (
              <Link to="/configuracoes" className="home-hero__pill">
                <FaCog /> Configurações
              </Link>
            )}
          </div>
        </section>

        {/* ── Grid de Cards ── */}
        <section className="home-grid">

          {/* Card: Exaustores com agendamento */}
          {isExaustoresEnabled && (
            <article className={`home-card ${isLoading ? 'home-card--loading' : ''}`}>
              <div className="home-card__head">
                <div className="home-card__iconbox home-card__iconbox--primary">
                  <PiFan />
                </div>
                <span className="home-card__label">Exaustores</span>
              </div>

              <div className="home-kpi">
                <span className="home-kpi__value">{exaustData.agendamentosAtivos}</span>
                <span className="home-kpi__sub">
                  {exaustData.agendamentosAtivos === 1 ? 'agendamento ativo' : 'agendamentos ativos'}
                </span>
              </div>

              <p className="home-card__detail">
                {exaustData.torresComAgendamento}/{exaustData.torresConsultadas} torres
                {exaustData.totalExaustores > 0 && ` · ${exaustData.totalExaustores} exaustores`}
              </p>

              <p className="home-card__note">
                Baseado em temporizadores — não indica se o exaustor está ligado no momento.
              </p>

              <div className="home-card__footer">
                <span className="home-card__meta">Atualizado: {formatarDataHora(exaustData.atualizacao)}</span>
                <Link className="home-card__link" to="/exaustao">
                  Ver <FaArrowRight size={9} />
                </Link>
              </div>
            </article>
          )}

          {/* Card: Níveis dos Reservatórios */}
          {isReservatoriosEnabled && (
            <article className={`home-card home-card--${reservStatus} ${isLoading ? 'home-card--loading' : ''}`}>
              <div className="home-card__head">
                <div className="home-card__iconbox home-card__iconbox--blue">
                  <FaWater />
                </div>
                <span className="home-card__label">Reservatórios</span>
              </div>

              <ul className="home-levels">
                <li className="home-levels__row home-levels__row--ok">
                  <span className="home-levels__bullet">✓</span>
                  <strong>{reservData.ok}</strong>
                  <span>dentro do limite</span>
                </li>
                <li className={`home-levels__row home-levels__row--${reservData.abaixoMin > 0 ? 'danger' : 'muted'}`}>
                  <span className="home-levels__bullet">↓</span>
                  <strong>{reservData.abaixoMin}</strong>
                  <span>abaixo do mínimo</span>
                </li>
                <li className={`home-levels__row home-levels__row--${reservData.acimaMax > 0 ? 'warning' : 'muted'}`}>
                  <span className="home-levels__bullet">↑</span>
                  <strong>{reservData.acimaMax}</strong>
                  <span>acima do máximo</span>
                </li>
                {reservData.semDados > 0 && (
                  <li className="home-levels__row home-levels__row--muted">
                    <span className="home-levels__bullet">–</span>
                    <strong>{reservData.semDados}</strong>
                    <span>sem leitura</span>
                  </li>
                )}
              </ul>

              <div className="home-card__footer">
                <span className="home-card__meta">Leitura: {formatarDataHora(reservData.ultimaLeitura)}</span>
                <Link className="home-card__link" to="/reservatorios">
                  Ver <FaArrowRight size={9} />
                </Link>
              </div>
            </article>
          )}

          {/* Card: Alertas ativos */}
          {isAlertasEnabled && (
            <article className={`home-card home-card--${alertasStatus} ${isLoading ? 'home-card--loading' : ''}`}>
              <div className="home-card__head">
                <div className={`home-card__iconbox home-card__iconbox--${alertasStatus}`}>
                  <FaBell />
                </div>
                <span className="home-card__label">Alertas Ativos</span>
              </div>

              <div className="home-kpi">
                <span className={`home-kpi__value home-kpi__value--${alertasStatus}`}>
                  {alertasData.totalOffline}
                </span>
                <span className="home-kpi__sub">
                  {alertasData.totalOffline === 0
                    ? 'tudo operando normalmente'
                    : alertasData.totalOffline === 1
                    ? 'módulo offline'
                    : 'módulos offline'}
                </span>
              </div>

              <div className="home-card__footer">
                <span className="home-card__meta">
                  Verificação: {formatarDataHora(alertasData.ultimaVerificacao)}
                </span>
                {alertasData.totalOffline > 0 && (
                  <Link className="home-card__link" to="/reservatorios">
                    Ver reservatórios <FaArrowRight size={9} />
                  </Link>
                )}
              </div>
            </article>
          )}

        </section>

        {/* ── Atualizar ── */}
        <div className="home-footer-row">
          <button
            type="button"
            className="home-refresh-btn"
            onClick={carregarTudo}
            disabled={isLoading}
          >
            <TfiReload size={13} className={isLoading ? 'home-spin' : ''} />
            Atualizar dados
          </button>
        </div>

      </div>
    </AppLayout>
  );
};

export default Home;
